import { useMounted } from "functions/Util";
import { useEffect, useState } from "react";
import mqtt from "mqtt";


const broadcastRoot = "pla";

const BrokerRE = /^mqtts?:\/\/(?<host>[^:/]+)(?::\d+)?.*/;

export enum LoginFailureModes {
  connection,
  credentials,
}

export enum MqttClientState {
	none,
	connecting,
	ok,
	err,
}

export const CONNECT_TIMEOUT = 4000;

export function useMqttClient(name: string | null, pass: string | null, broker: string) {
	const [state,  setState]  = useState                          (MqttClientState.none);
	const [client, setClient] = useState<mqtt.MqttClient | null>       (null);
	const [error,  setError]  = useState<LoginFailureModes | null>(null);
	const mounted = useMounted();

	const broadcastTopic = `${name}/${broadcastRoot}`;

	function Error(client: mqtt.MqttClient | null, err: unknown, kind: LoginFailureModes) {
		console.warn(err, "aborting login");
		client?.removeAllListeners("offline");
		client?.end(true);

		if (mounted.current) {
			let clearing = false;
			setClient(latest => {
				if (latest === client) clearing = true;
				if (clearing) return null;
				else return latest;
			});
			if (clearing) setError (kind);
		}
	}

	useEffect(function Connect() {
		setError(null);
		if (name === null || pass === null) {
			setState(MqttClientState.none);
			setClient(null);
			return;
		}

		const id = `mqtt_${Math.random().toString(16).slice(3)}`;
		const brokerHost = broker.match(BrokerRE)?.groups?.host ?? "broker";

		let client: mqtt.MqttClient | null = null;

		try {
			client = mqtt.connect(broker, {
				clientId: id,
				clean: true,
				connectTimeout: CONNECT_TIMEOUT,
				reconnectPeriod: 5000,
				username: `${name}@${brokerHost}`,
				password: pass,
				protocol: broker.startsWith("mqtts") ? "wss" : "ws",
			});

			client.subscribe(broadcastTopic);

			setClient(client);
			setState(MqttClientState.connecting);

			client.once("offline",               () => Error(client, undefined, LoginFailureModes.connection));
			(client as any).stream.once("error", () => Error(client, undefined, LoginFailureModes.connection));
			client.once("error", e  => {
				if (e.message === "connack timeout") Error(client, e, LoginFailureModes.connection);
				else Error(client, e, LoginFailureModes.credentials);
			});
			client.once("disconnect", () => Error(client, "Disconnected", LoginFailureModes.credentials));

			client.once("connect", () => {
				client?.removeAllListeners("error");
				client?.removeAllListeners("offline");
				client?.on("error", console.warn);

				if (mounted.current) setState(MqttClientState.ok);
			});
		} catch (e) {
			Error(client, e, LoginFailureModes.connection);
			setState(MqttClientState.err);
		}

		return function Disconnect() {
			setState(MqttClientState.none);
			if (client === null) return;
			client.end(true);

			if (mounted.current) {
				setError(null);
				setClient(null);
			}
		}
	}, [name, pass, broker]);

	return { client, state, error, broadcastTopic };
}


/*
class GetCacheEntry<D extends SupportedDevices, T extends Fetchable<D>> extends EventTarget {
	readonly request: T;

	active: boolean                 = false;
	lastData: DataType<D, T> | null = null;

	lastRespTime:  Date;

	oldestRequest: Date;
	newestRequest: Date;

	constructor(device: D, request: T) {
		super();

		this.request = request;
		this.lastRespTime  = this.newestRequest = this.oldestRequest = new Date(0);
	}

	public Update(val: DataType<D, T>) {
		if (StartsWith(this.request, "Harm")) {
			val = Object.keys(val).sort().map(k => (val as any)[k]) as DataType<D, T>;
		}

		this.lastRespTime = new Date();
		this.lastData     = val;

		this.dispatch(val);
	}

	private dispatch(detail: DataType<D, T>) {
		let ev = new Event("recv") as any;
		ev["detail"] = detail;
		return super.dispatchEvent(ev);
	}
}

type GetCacheEntries<D extends SupportedDevices> = {
	[T in Fetchable<D>]: GetCacheEntry<D, T>
};

function CreateGetCacheEntries<D extends SupportedDevices>(dvc: D) : GetCacheEntries<D> {
	const result = {} as any;

	for (let req of GetRequests[dvc]) {
		result[req] = new GetCacheEntry(dvc, req as any);
	}

	return result;
}

export class GetCache<D extends SupportedDevices> {
	readonly device : D;
	readonly entries: GetCacheEntries<D>;

	private readonly topicRX: string;
	private readonly topicTX: string;

	private readonly rcvCallback : OnMessageCallback;

	private readonly client;

	constructor (user: string, device: D, uid: string, client: MqttClient) {
		this.device = device;
		this.entries = CreateGetCacheEntries(device);
		this.topicTX = `${user}/${dvc_root[device]}/${uid}/${requestTopic}`;
		this.topicRX = `${user}/${dvc_root[device]}/${uid}/${responseTopic}`;

		this.client = client;
		this.rcvCallback = this.OnMessage.bind(this);
	}

	public Start() {
		this.client.addListener("message", this.rcvCallback);
		this.client.subscribe([this.topicRX, this.topicTX], console.log);
	}

	public Stop() {
		if (this.client.connected && !this.client.disconnecting) {
			this.client.unsubscribe([this.topicRX, this.topicTX]);
		}
		this.client.removeListener("message", this.rcvCallback);
	}

	private Error(kind: err_key, msg: string, ...args: any[]) {
		console.error("Mqtt error", kind, msg, ...args);
		// TODO: raise event
		return false;
	}

	private OnMessage(topic: string, payload: Buffer) {
		switch (topic) {
		case this.topicTX:
			// console.log("Tx", topic, payload.toString());
			break;

		case this.topicRX:
			this.ProcessIncoming(payload);
			break;

		default:
			console.warn("Unhandled topic", topic);
			break;
		}
	}

	private ProcessIncoming(payload: Buffer) {
		let o: any;
		try   { o = JSON.parse(payload.toString()) }
		catch { return this.Error("parse_fail", "Mqtt response is not valid json."); }

		if (typeof o !== "object") return this.Error("parse_fail", "Mqtt response is not object.");

		const head = o.Head;
		if (!IsResponseHead(head))         return this.Error("parse_fail", "Mqtt headless response.");
		if (!IsFromDevice(o, this.device)) return this.Error("parse_fail", "Mqtt response comes from wrong device type");

		if (DEBUG) console.debug("RECV", head.File_type, o);

		const cacheEntry = this.entries[head.File_type as Fetchable<D>];
		if (!cacheEntry) return this.Error("parse_fail", "Mqtt response to unknown request.", head.File_type);

		const data = o.Data;
		if (!CheckResponse(this.device, head.File_type as Fetchable<D>, data))
			return this.Error("parse_fail", "Mqtt response has wrong data format.");

		cacheEntry.Update(data);
	}

	public Discriminate<Dd extends SupportedDevices>(dvc: Dd) : this is GetCache<Dd> {
		return this.device as string === dvc;
	}
}
*/

